import React, { useEffect } from "react";
import { connect } from "react-redux";

import Result from "antd/es/result";
import { ColumnsType } from "antd/es/table";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import Table from "@app/component/Table";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import TextWithLine from "@app/component/typography/TextWithLine";
import Path from "@app/enum/Path";
import { saveIntegration } from "@app/store/integrations/actions";
import {
  getCurrentIntegration,
  getCustomFieldIntegrationResponse,
  getIntegrationSyncs,
  getSaving,
} from "@app/store/integrations/selectors";
import { ServiceDescription } from "@app/store/integrations/supported-services";
import { RootState } from "@app/store/rootReducer";
import Integration from "@app/types/integrations/Integration";
import IntegrationFieldResponse from "@app/types/integrations/IntegrationFieldResponse";
import IntegrationSync from "@app/types/integrations/IntegrationSync";

import IntegrationCard from "../IntegrationCard";
import IntegrationDetails from "../IntegrationDetails";

import styles from "./Steps.module.scss";
import useResultColumnConfig from "./useResultColumnConfig";

interface Props {
  customFieldResponse: IntegrationFieldResponse[];
  integration?: Integration;
  integrationSyncs: IntegrationSync[];
  saveIntegration: typeof saveIntegration.request;
  saving: boolean;
  selectedService: ServiceDescription;
}

const Step4: React.FC<Props> = ({
  customFieldResponse,
  integration,
  integrationSyncs,
  saveIntegration,
  saving,
  selectedService,
}) => {
  const intl = useIntl();

  const resultColumns: ColumnsType<IntegrationFieldResponse> = useResultColumnConfig(intl);

  useEffect(() => {
    saveIntegration();
  }, [saveIntegration]);

  if (saving) {
    return <LoadingSpinner global />;
  }

  return (
    <>
      <Result
        extra={[
          <Link key="back-button" to={Path.ROOT}>
            <Button key="return" type="primary">
              <FormattedMessage
                id="integrations.finalizeStep.returnButton"
                defaultMessage="Return to Dashboard"
                description="Text of return button"
              />
            </Button>
          </Link>,
        ]}
        status="success"
        subTitle={intl.formatMessage(
          {
            id: "integrations.finalizeStep.successMessageDescription",
            defaultMessage:
              "Thank you for mapping your records. Your integration will begin syncing according to the selected start date. If your integration’s status does not show “Completed” by the sync start, reach out to your Map My Customers account manager or send us an email at {email}",
            description: "Text of Success description",
          },
          {
            email: (
              <a href="mailto: integration@mapmycustomers.me">integration@mapmycustomers.me</a>
            ),
          }
        )}
        title={intl.formatMessage({
          id: "integrations.finalizeStep.successMessage",
          defaultMessage: "Your data is on it's way",
          description: "Text of Success message",
        })}
      />
      <IntegrationDetails
        integrationSync={integrationSyncs[0]}
        integrationSyncOptions={integration!.syncOptions}
        integrationType={integration!.type}
        isRealIntegration
        selectedService={selectedService}
      />
      {customFieldResponse.length ? (
        <div className={styles.customFieldsContainer}>
          <TextWithLine>
            <TextWithInfo
              info={intl.formatMessage({
                id: "integrations.finalizeStep.customFieldsCreated.tooltip",
                defaultMessage:
                  "These are the custom fields created during this integration setup. You can edit data types in app Settings",
                description: "Tooltip of Custom fields section",
              })}
            >
              {intl.formatMessage({
                id: "integrations.finalizeStep.customFieldsCreated",
                defaultMessage: "Custom fields created",
                description: "Text of Custom fields section",
              })}
            </TextWithInfo>
          </TextWithLine>
          <IntegrationCard>
            <Table<IntegrationFieldResponse>
              columns={resultColumns}
              dataSource={customFieldResponse}
              noHover
              pagination={false}
              rowKey="id"
            />
          </IntegrationCard>
        </div>
      ) : null}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  customFieldResponse: getCustomFieldIntegrationResponse(state),
  integration: getCurrentIntegration(state),
  integrationSyncs: getIntegrationSyncs(state),
  saving: getSaving(state),
});

const mapDispatchToProps = {
  saveIntegration: saveIntegration.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Step4);
